import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getAccessibleGuilds } from '../lib/api';
import { useAuth } from './AuthContext';

const AccessibleGuildsContext = createContext(null);
const ACCESSIBLE_GUILDS_FRESH_MS = 15_000;

export const AccessibleGuildsProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = String(user?.user_id || '');
  const [guilds, setGuilds] = useState([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState(null);
  const stateRef = useRef({ userId: '', guilds: [], fetchedAt: 0 });
  const inFlightRef = useRef(null);

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (!userId) {
      stateRef.current = { userId: '', guilds: [], fetchedAt: 0 };
      setGuilds([]);
      setError(null);
      setLoading(false);
      return [];
    }

    const current = stateRef.current;
    if (
      !force &&
      current.userId === userId &&
      current.fetchedAt > 0 &&
      Date.now() - current.fetchedAt < ACCESSIBLE_GUILDS_FRESH_MS
    ) {
      return current.guilds;
    }
    if (inFlightRef.current?.userId === userId) {
      return inFlightRef.current.promise;
    }

    setLoading(true);
    setError(null);
    const pending = getAccessibleGuilds()
      .then((result) => {
        const nextGuilds = Array.isArray(result) ? result : [];
        if (stateRef.current.userId !== userId) return [];
        stateRef.current = {
          userId,
          guilds: nextGuilds,
          fetchedAt: Date.now(),
        };
        setGuilds(nextGuilds);
        return nextGuilds;
      })
      .catch((nextError) => {
        if (stateRef.current.userId === userId) setError(nextError);
        throw nextError;
      })
      .finally(() => {
        if (inFlightRef.current?.promise === pending) inFlightRef.current = null;
        if (stateRef.current.userId === userId) setLoading(false);
      });

    inFlightRef.current = { userId, promise: pending };
    return pending;
  }, [userId]);

  useEffect(() => {
    if (stateRef.current.userId !== userId) {
      stateRef.current = { userId, guilds: [], fetchedAt: 0 };
      inFlightRef.current = null;
      setGuilds([]);
      setError(null);
      setLoading(Boolean(userId));
    }
    if (userId) refresh().catch(() => {});
  }, [refresh, userId]);

  useEffect(() => {
    const handleAccessUpdate = () => {
      stateRef.current.fetchedAt = 0;
      refresh({ force: true }).catch(() => {});
    };
    window.addEventListener('tns:dashboard-access-updated', handleAccessUpdate);
    return () => window.removeEventListener('tns:dashboard-access-updated', handleAccessUpdate);
  }, [refresh]);

  return (
    <AccessibleGuildsContext.Provider
      value={{
        guilds,
        loading: loading || (Boolean(userId) && stateRef.current.userId !== userId),
        error,
        refresh,
      }}
    >
      {children}
    </AccessibleGuildsContext.Provider>
  );
};

export const useAccessibleGuilds = () => {
  const value = useContext(AccessibleGuildsContext);
  if (!value) {
    throw new Error('useAccessibleGuilds must be used within AccessibleGuildsProvider');
  }
  return value;
};
